import React, { useContext, useEffect, useState } from 'react'
import useFetch from '../hooks/useFetch'
import UserContext from '../contexts/UserContext';
import { getCurrentSubscriptionPrice, getMyBillingInfo, postUpgradeSubscription, postUpsertBillingoPartner } from '../config/apiRoutes';
import { BillingoPartner, BillingoPartnerTaxType, PostBillingoPartnerDto } from '../models/BillingoPartner';
import { Checkbox, FormControlLabel, FormGroup } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { SubscriptionPlanPrice } from '../models/SubscriptionPlanPrice';
import useBillingoPartnerValidation from '../hooks/billing/useBillingoPartnerValidation';
import PageContainer from '../containers/PageContainer';
import PageTitleCard from '../cards/PageTitleCard';
import PageTitleText from '../texts/PageTitleText';
import StandardTextInput from '../inputs/StandardTextInput';
import SectionTitle from '../texts/SectionTitle';
import PrimaryButton from '../buttons/PrimaryButton';
import PaymentIcon from '@mui/icons-material/Payment';
import { paymentSettingsRoute, privacyPolicyRoute, termsAndConditionsRoute, usePathReconstructor } from '../config/pageRoutes';
import SnackbarContext from '../contexts/SnackbarContext';
import { primary } from '../styles/colors';

type Props = {}

const SubscriptionPage = (props: Props) => {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const { selectedRestaurant } = useContext(UserContext);
  const billingDataCaller = useFetch();
  const priceCaller = useFetch();
  const paymentCaller = useFetch();
  const { showMessage } = useContext(SnackbarContext);
  const { copyParamsFromPath } = usePathReconstructor();
  const [isPrivacyPolicyAccepted, setIsPrivacyPolicyAccepted] = useState(false);
  const [isTermsAccepted, setIsTermsAccepted] = useState(false);
  const [isRecurringAccepted, setIsRecurringAccepted] = useState(false);
  const [billingoPartner, setBillingoPartner] = useState<BillingoPartner>({
    id: -1,
    name: "",
    tax_type: BillingoPartnerTaxType.NO_TAX_NUMBER,
    taxcode: "",
    phone: "",
    address: {
      country_code: "HU",
      post_code: "",
      city: "",
      address: ""
    },
    emails: []
  });
  const [price, setPrice] = useState<SubscriptionPlanPrice | null>(null);
  const {
    isValidationEnabled,
    setIsValidationEnabled,
    isNameValid,
    isCityValid,
    isPostalCodeValid,
    isAddressValid,
    isBillingInfoValid,
    getBillingInfoValid
  } = useBillingoPartnerValidation(billingoPartner);
  const [emailsString, setEmailsString] = useState(billingoPartner.emails.join(","));

  useEffect(() => {
    document.title = `QuickPick - ${t("navigation.subscription")}`;
  }, [t("navigation.subscription")]);

  useEffect(() => {
    fetchBillingInfo();
  }, [selectedRestaurant]);

  useEffect(() => {
    fetchPrice();
  }, [searchParams]);

  useEffect(() => {
    if (paymentCaller.errorMessage && paymentCaller.error) {
      showMessage(paymentCaller.errorMessage, "error");
    }
  }, [paymentCaller]);

  async function fetchBillingInfo() {
    if (selectedRestaurant == null) return;
    const response = await billingDataCaller.call<any, BillingoPartner>("GET", getMyBillingInfo.replace(":restaurantShortName", selectedRestaurant.restaurantShortName));
    if (response.ok && response.data) {
      setBillingoPartner(response.data as BillingoPartner);
    }
  }

  async function fetchPrice() {
    const priceId = searchParams.get("price_id");
    if (!priceId) return;
    const response = await priceCaller.call<any, SubscriptionPlanPrice>("GET", getCurrentSubscriptionPrice.replace(":id", priceId));
    if (response.ok && response.data) {
      setPrice(response.data);
    }
  }

  async function handlePaymentClick() {
    if (!selectedRestaurant) return;
    setIsValidationEnabled(true);
    if (price == null) return;
    if (!getBillingInfoValid()) {
      return;
    }
    const response = await paymentCaller.call<any, PostBillingoPartnerDto>(
      "POST",
      postUpsertBillingoPartner.replace(":restaurantShortName", selectedRestaurant.restaurantShortName),
      {
        name: billingoPartner.name,
        address: {
          country_code: billingoPartner.address.country_code,
          post_code: billingoPartner.address.post_code,
          city: billingoPartner.address.city,
          address: billingoPartner.address.address
        },
        phone: billingoPartner.phone,
        taxcode: billingoPartner.taxcode,
        emails: getEmailsArrayFromString(emailsString)
      });
    if (response.ok && response.data) {
      handlePostPayment();
    }
  }

  function getEmailsArrayFromString(emailsString: string): string[] {
    return emailsString
      .split(',')
      .map(email => email.trim())
      .filter(email => email.length > 0);
  }

  async function handlePostPayment() {
    if (selectedRestaurant == null) return;
    if (price == null) return;
    const response = await paymentCaller.call<any, any>("POST",
      postUpgradeSubscription.replace(":restaurantShortName", selectedRestaurant.restaurantShortName),
      {
        redirectUrl: window.location.origin + copyParamsFromPath(paymentSettingsRoute.path),
        priceId: price.id,
        locale: i18n.language
      });
    if (response.ok && response.data) {
      const url = response.data.redirectUrl;
      window.location.href = url;
    }
  }

  useEffect(() => {
    const script = document.createElement('script');
    script.src = '/scripts/barion.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  function handleBillingEmailsChange(emailsString: string) {
    setEmailsString(emailsString);
  }

  useEffect(() => {
    setEmailsString(billingoPartner.emails.join(","));
  }, [billingoPartner]);

  return (
    <PageContainer>
      <PageTitleCard>
        <PageTitleText>
          {t("payment.subscription_page_title")}
        </PageTitleText>
      </PageTitleCard>
      <div className="mt-5">
        {price && (
          <div className="bg-primary-light rounded-[8px] shadow-md px-2 py-2 text-white flex gap-3">
            <div className="grow">
              <div className="font-bold text-[16px]">
                {t(`payment.subscription_plans.${price?.planId}`)} - {t(`payment.subscription_interval_payment.${price.interval}`)}
              </div>
              <div>
                {t("payment.plan_descriptions." + price?.planId)}
              </div>
            </div>
            <div className=" mt-1 py-2 px-2 h-fit bg-primary rounded-[8px] text-white font-bold text-right break-keep">
              {price.priceGross.toLocaleString()}&nbsp;Ft
            </div>
          </div>
        )}
      </div>
      <SectionTitle>
        {t("payment.billing.billing_info")}
      </SectionTitle>
      <div>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2 md:col-span-1">
            <StandardTextInput
              label={t("payment.billing.name")}
              value={billingoPartner.name}
              onChange={(e) => { setBillingoPartner({ ...billingoPartner, name: e.target.value }) }}
              required
              helperText={t("common.validation.required")}
              error={!isNameValid && isValidationEnabled}
            />
          </div>
          <div className="col-span-2 md:col-span-1">
            <StandardTextInput
              label={t("payment.billing.tax_number")}
              value={billingoPartner.taxcode}
              onChange={(e) => { setBillingoPartner({ ...billingoPartner, taxcode: e.target.value }) }}
              helperText={t("payment.billing.tax_number_helper_text")}
            />
          </div>
        </div>
        <div className="grid grid-cols-2 ">
          <div className="col-span-2 md:col-span-1">
            <StandardTextInput
              label={t("payment.billing.country")}
              value={t("common.hungary")}
              disabled
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2 md:col-span-1">
            <StandardTextInput
              label={t("payment.billing.postal_code")}
              value={billingoPartner.address.post_code}
              onChange={(e) => { setBillingoPartner({ ...billingoPartner, address: { ...billingoPartner.address, post_code: e.target.value } }) }}
              helperText={t("common.validation.required")}
              error={!isPostalCodeValid && isValidationEnabled}
            />
          </div>
          <div className="col-span-2 md:col-span-1">
            <StandardTextInput
              label={t("payment.billing.city")}
              value={billingoPartner.address.city}
              onChange={(e) => { setBillingoPartner({ ...billingoPartner, address: { ...billingoPartner.address, city: e.target.value } }) }}
              helperText={t("common.validation.required")}
              error={!isCityValid && isValidationEnabled}
            />
          </div>
          <div className="col-span-2 md:col-span-1">
            <StandardTextInput
              label={t("payment.billing.address")}
              value={billingoPartner.address.address}
              onChange={(e) => { setBillingoPartner({ ...billingoPartner, address: { ...billingoPartner.address, address: e.target.value } }) }}
              helperText={t("common.validation.required")}
              error={!isAddressValid && isValidationEnabled}
            />
          </div>
          <div className="col-span-2 md:col-span-1">
            <StandardTextInput
              label={t("payment.billing.phone")}
              value={billingoPartner.phone}
              onChange={(e) => { setBillingoPartner({ ...billingoPartner, phone: e.target.value }) }}
              helperText={t("common.validation.optional")}
            />
          </div>
          <div className="col-span-2 md:col-span-1">
            <StandardTextInput
              label={t("auth.email")}
              value={emailsString}
              onChange={(e) => { handleBillingEmailsChange(e.target.value) }}
              helperText={t("payment.billing.emails_helper_text")}
            />
          </div>
        </div>
      </div>
      <div>
        <div>
          <FormGroup>
            <FormControlLabel control={<Checkbox
              checked={isTermsAccepted}
              style={{
                color: primary.dark
              }}
              onChange={(e) => { setIsTermsAccepted(e.target.checked) }}
            />} label={<div className="text-[15px] mt-[2px]">
              <Trans
                i18nKey={"payment.accept_terms_and_conditions"}
                components={{ a: <a target="_blank" className="text-primary hover:underline" href={copyParamsFromPath(termsAndConditionsRoute.path)} /> }}
              />
            </div>} />
          </FormGroup>
          <FormGroup>
            <FormControlLabel control={<Checkbox
              style={{
                color: primary.dark
              }}
              checked={isPrivacyPolicyAccepted}
              onChange={(e) => { setIsPrivacyPolicyAccepted(e.target.checked) }}
            />} label={<div className="text-[15px] mt-[2px]">
              <Trans
                i18nKey={"payment.accept_privacy_policy"}
                components={{ a: <a target="_blank" className="text-primary hover:underline" href={copyParamsFromPath(termsAndConditionsRoute.path)} /> }}
              />
            </div>} />
          </FormGroup>
          <FormGroup>
            <FormControlLabel control={<Checkbox
              style={{
                color: primary.dark
              }}
              checked={isRecurringAccepted}
              onChange={(e) => { setIsRecurringAccepted(e.target.checked) }}
            />} label={<div className="text-[15px] mt-[2px]">
              <Trans
                i18nKey={"payment.accept_recurring_payments"}
              />
            </div>} />
          </FormGroup>
        </div>
      </div>
      <div className="mb-5 mt-3">
        <PrimaryButton
          variant="strike"
          onClick={handlePaymentClick}
          disabled={(!isBillingInfoValid && isValidationEnabled) || !isTermsAccepted || !isPrivacyPolicyAccepted || !isRecurringAccepted}
          startIcon={<PaymentIcon />}
        >
          {t("payment.pay")}
        </PrimaryButton>
        <img className="h-[50px]" src="/assets/barion-banner-lightmode.svg" alt="barion-banner" />
      </div>
    </PageContainer>
  )
}

export default SubscriptionPage